import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import clsx from "clsx";
import { Typography, Avatar, CircularProgress, Tooltip } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import Resizer from "react-image-file-resizer";

import {
  DR_DRIVER_TYPE,
  CHOICE_DRIVER_TYPE,
  ROBOT_DRIVER_TYPE,
} from "../../../consts/driverTypes";

import { validateDriverProfileEdit } from "../../../utils/drivers";

import { selectedDriverSelector } from "../../../reducers/ownApp/selectors";
import { addNotification } from "../../../reducers/ownApp";
import { lookupTableSelector } from "../../../reducers/depot/selectors";
import {
  driverEditSelector,
  driverImagesSelector,
  driverTypesSelector,
} from "../../../reducers/driverManagement/selectors";
import {
  fetchDriverImage,
  updateDriverImage,
  setIsDriverEditProfileValid,
  updateDriverProfileEdit,
} from "../../../reducers/driverManagement/thunkActions";

import { View } from "../../View";
import { palette } from "../../../theme/palette";
import { NONE } from "../../../consts/strings";

import { DriverProfileModalContent } from "./DriverProfileModalContent";
import { AppProfileModalContent } from "./AppProfileModalContent";

const useStyles = makeStyles({
  container: {
    margin: "0.5%",
    maxWidth: "62rem",
    maxHeight: "99%",
    backgroundColor: palette.white,
    borderRadius: "0.4rem",
    display: "flex",
    flexDirection: "column",
    zIndex: 101,
    overflowX: "scroll",
  },
  hidden: {
    display: "none",
  },
  topHeader: {
    display: "flex",
    flexDirection: "row-reverse",
    backgroundColor: palette.dpdLightGrey,
  },
  bottomHeader: {
    marginTop: "1rem",
    backgroundColor: palette.dpdLightGrey,
  },
  title: {
    padding: "1.2rem 1.6rem",
  },
  titlePlaceholder: {
    width: "8rem",
  },
  rowHeader: {
    color: palette.white,
    textAlign: "center",
    padding: "0.84rem 0",
  },
  rowHeaderSmall: {
    color: palette.white,
    textAlign: "center",
    padding: "0.25rem 0",
    fontSize: 13,
  },
  content: {
    display: "flex",
    marginTop: "0.5rem",
  },
  topContent: {
    flex: 1,
    display: "flex",
    padding: "0.5rem 0",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: "1rem",
  },
  avatarContainer: {
    width: "50px",
    height: "50px",
  },
  avatar: {
    width: "50px",
    height: "50px",
  },
  editAvatar: {
    width: "47px",
    height: "47px",
    borderStyle: "solid",
    borderWidth: "3px",
    borderColor: palette.orange,
  },
  headerText: {
    fontWeight: "500",
    fontSize: "1.2rem",
  },
});

export const DriverProfileModal = ({ hidden = false }) => {
  const styles = useStyles();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errors, setErrors] = useState({});
  const inputRef = useRef();
  const dispatch = useDispatch();
  const lookupTables = useSelector(lookupTableSelector);
  const driverEdit = useSelector(driverEditSelector);
  const selectedDriver = useSelector(selectedDriverSelector);
  const driverImages = useSelector(driverImagesSelector);
  const driverTypes = useSelector(driverTypesSelector);
  const selectedDriverCode = (driverEdit || selectedDriver)?.driverCode;
  const hasSelectedDriver = !!driverEdit || !!selectedDriver;
  const loadingDriverImage = "pending" === driverImages[selectedDriverCode];
  const hasDriverImage =
    !!driverImages[selectedDriverCode] &&
    !["pending", "rejected"].includes(driverImages[selectedDriverCode]);

  const onChange = async ({ updates = [] }) => {
    // This prevents users from editing driver more then once before driver is locked
    // what could potentially lead to 2nd driver lock request being send
    // for the same driver before the first one has been settled
    if (isProcessing) return;
    setIsProcessing(true);
    const result = await dispatch(
      updateDriverProfileEdit({
        updates: updates.map(({ name, value }) => ({
          name,
          value: value === NONE ? null : value,
        })),
      })
    );
    setIsProcessing(false);
    return result;
  };

  useEffect(() => {
    if (!driverEdit && !selectedDriver) return;
    const { errors } = validateDriverProfileEdit({
      driverEdit: driverEdit || selectedDriver,
      lookupTables,
    });
    setErrors(errors);
    dispatch(setIsDriverEditProfileValid(!Object.keys(errors).length));
  }, [dispatch, driverEdit, selectedDriver, lookupTables]);

  useEffect(() => {
    if (!selectedDriverCode || !!driverImages[selectedDriverCode]) return;
    dispatch(fetchDriverImage({ driverCode: selectedDriverCode }));
  }, [dispatch, driverImages, selectedDriverCode]);

  if (!hasSelectedDriver) return null;

  const { driverProfileEdit } = driverEdit || selectedDriver;
  const { driverName, driverCode, driverType } = driverProfileEdit;
  const disabled = !driverEdit || driverType === ROBOT_DRIVER_TYPE;

  const onDriverPhotoUpload = async ({ target }) => {
    const [file] = target.files;
    if (!file) return;
    else if (!["image/png", "image/jpg", "image/jpeg"].includes(file.type))
      return dispatch(
        addNotification({
          message: `Invalid file type ${file.type}. Please provide a png/jpg/jpeg image.`,
          options: { variant: "error" },
        })
      );
    // Reads, resize and encodes the image
    const image = await new Promise((resolve, reject) => {
      try {
        Resizer.imageFileResizer(
          file,
          500,
          500,
          "JPEG",
          80,
          0,
          (blob) => resolve(blob),
          "base64"
        );
      } catch (error) {
        reject(error);
      }
    });
    await dispatch(
      updateDriverImage({ driverCode: driverProfileEdit.driverCode, image })
    );
  };

  return (
    <View className={clsx(styles.container, hidden && styles.hidden)}>
      <View className={styles.topHeader}>
        <View className={styles.titlePlaceholder} />
        <View className={styles.topContent}>
          <Typography className={styles.headerText}>
            {driverName} ({driverCode.split("*")[1]})
          </Typography>
          <View className={styles.avatarContainer}>
            {loadingDriverImage ? (
              <CircularProgress />
            ) : (
              <Tooltip
                title={!!driverEdit ? "Upload driver photo" : ""}
                placement={"right"}
                enterDelay={500}
                enterNextDelay={500}
              >
                <Avatar
                  className={!driverEdit ? styles.avatar : styles.editAvatar}
                  alt="User photo"
                  src={hasDriverImage ? driverImages[selectedDriverCode] : null}
                  onClick={() => !!driverEdit && inputRef.current.click()}
                />
              </Tooltip>
            )}
            <input
              ref={inputRef}
              className={styles.hidden}
              type="file"
              accept=".jpg,.jpeg,.png"
              onChange={(...args) => onDriverPhotoUpload(...args)}
            />
          </View>
        </View>
        <Typography className={styles.title}>{"DRIVER PROFILE"}</Typography>
      </View>
      <View className={styles.content}>
        <DriverProfileModalContent
          disabled={disabled}
          allowVehicleTypeChange={
            ![DR_DRIVER_TYPE, ROBOT_DRIVER_TYPE].includes(
              driverProfileEdit.driverType
            )
          }
          showAlternativeLocation={
            driverProfileEdit.driverType === CHOICE_DRIVER_TYPE
          }
          driverTypes={driverTypes}
          driverProfileEdit={driverProfileEdit}
          onChange={onChange}
          errors={errors}
        />
      </View>
      <View className={styles.bottomHeader}>
        <Typography className={styles.title}>{"APP PROFILE"}</Typography>
      </View>
      <View className={styles.content}>
        <AppProfileModalContent
          disabled={disabled}
          driverProfileEdit={driverProfileEdit}
          onChange={onChange}
          errors={errors}
        />
      </View>
    </View>
  );
};

export default DriverProfileModal;
